import { useMutation } from '@apollo/client';
import { ArchiveOrgUserDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import { useArchiveUserTRPC } from './useArchiveUserTRPC';

/**
 * Archive a non-SCIM org user, dual-routing between the tRPC stack and the
 * legacy Hasura/Apollo path based on the `trpc` feature flag. Server-side
 * guards (self-archive, SCIM, status precondition) are enforced on both paths;
 * this hook is a thin presentation-side wrapper.
 *
 * The Apollo path lives inline here (rather than a separate `*GraphQL` hook) so
 * the auto-migration tracker detects `ArchiveOrgUserDocument` in this flag-gated
 * bridge file — matching the canonical 2-file mutation pattern (see
 * `useDeleteActions`). `refetchQueries: ['GetAuthUsers']` is by operation name
 * so it matches the active observable regardless of variables.
 */
export const useArchiveUser = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  const [archiveUserGraphQL, graphqlState] = useMutation(
    ArchiveOrgUserDocument,
    {
      refetchQueries: ['GetAuthUsers'],
      awaitRefetchQueries: true,
      update: (cache) => {
        evictField(cache, 'auth_user');
        evictField(cache, 'auth_user_aggregate');
      },
    }
  );

  const trpc = useArchiveUserTRPC();

  const archiveUser = async (userId: string) => {
    if (trpcEnabled) {
      return trpc.archiveUser(userId);
    }

    const result = await archiveUserGraphQL({ variables: { userId } });
    if (!result.data?.archive_org_user) {
      throw new Error('Archive user mutation returned no data');
    }

    return result.data.archive_org_user;
  };

  return {
    archiveUser,
    loading: trpcEnabled ? trpc.loading : graphqlState.loading,
    error: trpcEnabled ? trpc.error : graphqlState.error,
  };
};
